import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { END_REASON_LABEL, type MatchSummary } from '@bn/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Alert, Badge, Card, Spinner } from '@/components/ui';

const dateFormat = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

export function HistoryPage() {
  const { user } = useAuth();
  const [matches, setMatches] = useState<MatchSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<{ matches: MatchSummary[] }>('/api/history')
      .then((r) => setMatches(r.matches))
      .catch(() => setError('Impossible de charger l’historique.'));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-100">Historique</h1>
        <p className="text-sm text-slate-400">Vos parties, de la plus récente à la plus ancienne.</p>
      </div>

      {error && <Alert>{error}</Alert>}

      {matches === null ? (
        <Spinner />
      ) : matches.length === 0 ? (
        <Card>
          <p className="text-sm text-slate-400">Vous n’avez encore joué aucune partie.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {matches.map((match) => {
            const me = match.players.find((p) => p.userId === user?.id);
            const opponent = match.players.find((p) => p.userId !== user?.id);
            const delta =
              me?.ratingAfter != null && me.ratingBefore != null
                ? me.ratingAfter - me.ratingBefore
                : null;
            const issue =
              match.status === 'active'
                ? 'en cours'
                : match.winnerId === null
                  ? 'nulle'
                  : match.winnerId === user?.id
                    ? 'victoire'
                    : 'défaite';

            return (
              <Link
                key={match.id}
                to={`/partie/${match.id}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-700/60 bg-slate-900/60 px-4 py-3 transition hover:border-sky-500/60"
              >
                <div className="flex items-center gap-3">
                  <Badge
                    tone={
                      issue === 'victoire'
                        ? 'succes'
                        : issue === 'défaite'
                          ? 'alerte'
                          : issue === 'en cours'
                            ? 'info'
                            : 'neutre'
                    }
                  >
                    {issue}
                  </Badge>
                  <span className="text-sm text-slate-200">
                    contre {opponent?.displayName ?? 'inconnu'}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-400">
                  {match.endReason && <span>{END_REASON_LABEL[match.endReason]}</span>}
                  {delta !== null && (
                    <span className={delta >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                      {delta >= 0 ? '+' : ''}
                      {delta} pts
                    </span>
                  )}
                  <span>{dateFormat.format(new Date(match.createdAt))}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
